'use client';
import base, { AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { AuthContext, clearStorage } from './Authentication';
import { useRBACOptional } from '@/contexts/RBACContext';
import { fetchSubTenantMemberUserIds } from '@/lib/subTenantMemberUserIds';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { extractApiMessage } from './Axios';
import {
  getActiveTenantHeaders,
  readActiveTenantScope,
  resolveActiveRequestUserId,
} from '@/lib/activeTenantScope';

export const AudienceBuilderAxiosContext = createContext<AxiosInstance | null>(null);

/** SWR key segment: sub-tenant scope, member IDs, or account (e.g. reset to owner). */
export const AudienceBuilderSubTenantMemberKeyContext = createContext<string>('');

export const isCancel = base.isCancel;

/** Path only: handles absolute URLs and optional API prefix (e.g. /v1/purchases/...). */
function normalizeAudienceBuilderRequestPath(url: string | undefined): string {
  if (!url) return '';
  const noQuery = url.split('?')[0];
  if (noQuery.includes('://')) {
    try {
      const p = new URL(noQuery).pathname.replace(/^\/+/, '');
      return p;
    } catch {
      return noQuery.replace(/^\/+/, '');
    }
  }
  return noQuery.replace(/^\/+/, '');
}

function applyMemberUserIdsQuery(
  url: string,
  scopeActive: boolean,
  memberIds: number[] | null
): string {
  const pathPart = url.split('?')[0];
  const qs = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const sp = new URLSearchParams(qs);
  if (!scopeActive) {
    sp.delete('member_user_ids');
    const next = sp.toString();
    return next ? `${pathPart}?${next}` : pathPart;
  }
  const ids = memberIds ?? [];
  sp.set('member_user_ids', ids.map((n) => String(n)).join(','));
  return `${pathPart}?${sp.toString()}`;
}

/** GET purchases/user/{id} — audience dashboard list */
function isPurchasesUserListGetUrl(url: string | undefined): boolean {
  const path = normalizeAudienceBuilderRequestPath(url);
  return /(^|\/)purchases\/user\/[^/?]+$/.test(path);
}

function isListUserAudiencesPostUrl(url: string | undefined): boolean {
  const path = normalizeAudienceBuilderRequestPath(url);
  return /(^|\/)purchases\/list\/user_audiences$/.test(path);
}

function rewritePurchasesUserListPath(url: string, sessionUserId: string | number): string {
  const pathPart = url.split('?')[0];
  const qs = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const rewritten = pathPart.replace(
    /(^|\/)purchases\/user\/[^/?]+$/,
    `$1purchases/user/${encodeURIComponent(String(sessionUserId))}`
  );
  return qs ? `${rewritten}?${qs}` : rewritten;
}

function getAudienceBuilderInstance(
  auth: {
    userId?: string | number | null;
    token?: string | null;
    isAuthenticated?: boolean;
  },
  setSessionExpiredOpen: (open: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  listingSubAccountIdRef: MutableRefObject<number | null>
) {
  const axios = base.create({
    baseURL: (process.env.NEXT_PUBLIC_AUDIENCE_BUILDER_API_URL ||
      process.env.NEXT_PUBLIC_AUDIENCE_SERVICE_URL || '').replace(/\/+$/, '') + '/',
    withCredentials: true,
  });

  const auth_exclude: Array<string> = [];

  axios.interceptors.request.use((req) => {
    const method = (req.method || 'get').toLowerCase();
    const isImpersonating =
      typeof window !== 'undefined' && window.localStorage.getItem('impersonation') === 'true';
    const tenantScope = readActiveTenantScope();
    const scopedAccountId =
      listingSubAccountIdRef.current ??
      tenantScope.activeAccountId ??
      tenantScope.rootListingAccountId ??
      null;
    const subTenantScopeActive =
      (subTenantScopeActiveRef.current || scopedAccountId != null) && !isImpersonating;

    // Aggregate lists must key off the session user (owner), not the switched member.
    // member_user_ids carries the org-wide set; rewriting path/body to the member drops
    // owner NULL-account_id rows and can 403 when session ≠ path user.
    const switchedMemberId = tenantScope.activeUserId;
    const sessionUserId = auth.userId ?? null;
    const useSessionUserForAggregate =
      subTenantScopeActive &&
      sessionUserId != null &&
      switchedMemberId != null &&
      String(switchedMemberId) !== String(sessionUserId);
    const activeUserId = useSessionUserForAggregate
      ? sessionUserId
      : resolveActiveRequestUserId(auth.userId);

    if (method === 'get' && req.url && isPurchasesUserListGetUrl(req.url)) {
      if (sessionUserId != null && subTenantScopeActive) {
        req.url = rewritePurchasesUserListPath(req.url, sessionUserId);
      }
      req.url = applyMemberUserIdsQuery(
        req.url,
        subTenantScopeActive,
        aggregateMemberIdsRef.current
      );
    }

    if (method === 'post' && req.url && isListUserAudiencesPostUrl(req.url)) {
      if (!(req.data instanceof FormData)) {
        if (!req.data || typeof req.data !== 'object') {
          req.data = {};
        }
        const body = req.data as Record<string, unknown>;
        if (subTenantScopeActive) {
          const ids = aggregateMemberIdsRef.current ?? [];
          body.member_user_ids = ids.map((n) => String(n));
          if (sessionUserId != null) {
            body.user_id = String(sessionUserId);
          }
        } else {
          delete body.member_user_ids;
        }
      }
    }

    if (!req.data) {
      req.data = {};
    }
    if (!req.data.userID && activeUserId) {
      if (req.data instanceof FormData) {
        req.data.append('userID', String(activeUserId));
      } else {
        req.data.userID = activeUserId;
      }
    }
    if (auth?.isAuthenticated && auth_exclude.indexOf(req.url!) === -1) {
      if (!req.data.session_token && !(req.data instanceof FormData)) {
        req.data.session_token = auth.token;
      }
    }
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    Object.assign(req.headers, getActiveTenantHeaders());
    if (scopedAccountId != null && !req.headers['X-Active-Account-Id']) {
      req.headers['X-Active-Account-Id'] = String(scopedAccountId);
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth?.isAuthenticated) {
            location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError = (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError =
          'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function AudienceBuilderAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const rbac = useRBACOptional();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);
  const listingSubAccountIdRef = useRef<number | null>(null);

  const tenantScope = readActiveTenantScope();
  const listingSubAccountId =
    rbac?.effectiveListingSubAccountId ??
    (rbac?.isRootContextActive && rbac?.defaultAccountId != null ? rbac.defaultAccountId : null) ??
    rbac?.selectedSubAccountId ??
    tenantScope.activeAccountId ??
    tenantScope.rootListingAccountId ??
    null;
  const isImpersonating =
    typeof window !== 'undefined' && window.localStorage.getItem('impersonation') === 'true';
  const isSubTenantListingScope =
    (Boolean(listingSubAccountId) || Boolean(tenantScope.isAggregateSubTenantScope)) &&
    !isImpersonating;
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }
  subTenantScopeActiveRef.current = isSubTenantListingScope;
  listingSubAccountIdRef.current = listingSubAccountId;

  const ownerUserIdRef = useRef<number | null>(null);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id
    ? Number(rbac.userContext.owner_user_id)
    : null;
  const defaultAccountIdRef = useRef<number | null>(null);
  defaultAccountIdRef.current = rbac?.defaultAccountId ?? null;

  useEffect(() => {
    if (!listingSubAccountId || isImpersonating) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = rbac?.currentOrgId;
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Include org owner so root-member direct login still sees owner-created audiences.
        const ownerExtId = ownerUserIdRef.current;
        const rootAccId = defaultAccountIdRef.current;
        if (
          ownerExtId != null &&
          Number.isFinite(ownerExtId) &&
          (subId === rootAccId || tenantScope.rootListingAccountId === subId) &&
          !ids.includes(ownerExtId)
        ) {
          ids.push(ownerExtId);
        }
        if (!cancelled) {
          aggregateMemberIdsRef.current = ids;
          const sorted = [...ids].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isImpersonating, listingSubAccountId, rbac?.currentOrgId, tenantScope.rootListingAccountId]);

  const auth = authData || {};
  const client = useMemo(
    () =>
      getAudienceBuilderInstance(
        auth,
        setSessionExpiredOpen,
        aggregateMemberIdsRef,
        subTenantScopeActiveRef,
        listingSubAccountIdRef
      ),
    [authData, isImpersonating] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const effectiveMemberKey = isSubTenantListingScope ? subTenantMemberKey : '';
  const audienceBuilderSubTenantScopeSignal = [
    effectiveMemberKey,
    String(listingSubAccountId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
    String(tenantScope.rootListingAccountId ?? ''),
  ].join('|');

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    if (typeof window !== 'undefined') {
      location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
    }
  };

  return (
    <>
      <AudienceBuilderAxiosContext.Provider value={client}>
        <AudienceBuilderSubTenantMemberKeyContext.Provider value={audienceBuilderSubTenantScopeSignal}>
          {children}
        </AudienceBuilderSubTenantMemberKeyContext.Provider>
      </AudienceBuilderAxiosContext.Provider>

      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
